import type { WritableAtom } from 'jotai';
import { type Atom, atom } from 'jotai';
import type { Logger } from '../..';
import type { BaseActionType, UndoRedoActionCommand, UndoRedoActions, UndoRedoEvents } from './types';
export const undoRedoAtoms = <MetaDataT, ActionT extends BaseActionType>(actionsConfig: UndoRedoActions<MetaDataT, ActionT>, loggerAtom?: Atom<Logger | undefined>): {
  dispatch: WritableAtom<undefined, [action: ActionT | UndoRedoEvents], Promise<void> | void>;
  canUndo: Atom<boolean>;
  canRedo: Atom<boolean>;
  lastUndoableAction: Atom<ActionT | null>;
  lastRedoableAction: Atom<ActionT | null>;
} => {
  const stackAtom = atom<{
    command: UndoRedoActionCommand<ActionT, MetaDataT>;
    payload: ActionT;
  }[]>([]);
  const redoStackAtom = atom<{
    command: UndoRedoActionCommand<ActionT, MetaDataT>;
    payload: ActionT;
  }[]>([]);
  const canUndo = atom(get => get(stackAtom).length > 0);
  const canRedo = atom(get => get(redoStackAtom).length > 0);
  const dispatch = atom(undefined, async (get, set, action: ActionT | {
    type: 'UNDO';
  } | {
    type: 'REDO';
  }, afterActionSuccess?: (metaData: MetaDataT) => void) => {
    const logger = loggerAtom ? get(loggerAtom) : undefined;
    switch (action.type) {
      case 'UNDO':
        {
          const stack = get(stackAtom);
          const toUndo = stack.pop();
          if (!toUndo) {
            return;
          }
          const {
            command,
            payload
          } = toUndo;
          const undoResult = command.undo?.(payload, afterActionSuccess);
          if (undoResult instanceof Promise) {
            await undoResult;
          }
          set(stackAtom, [...stack]);
          set(redoStackAtom, [...get(redoStackAtom), toUndo]);
          return;
        }
      case 'REDO':
        {
          const redoStack = get(redoStackAtom);
          const toRedo = redoStack.pop();
          if (!toRedo) {
            return;
          }
          const {
            command,
            payload
          } = toRedo;

          // Call redo function only when provided, else call execute.
          const redoResult = command.redo ? command.redo(payload, afterActionSuccess) : command.execute(payload, afterActionSuccess);
          if (redoResult instanceof Promise) {
            await redoResult;
          }
          set(redoStackAtom, [...redoStack]);
          set(stackAtom, [...get(stackAtom), toRedo]);
          return;
        }
    }
    const {
      type
    } = action;
    if (type in actionsConfig) {
      const command = actionsConfig[(type as keyof typeof actionsConfig)](get, set, logger);
      // Typescript not able to infer the type of the payload will be correct due to the if check.
      const payload = (action as Extract<ActionT, {
        type: typeof type;
      }>);
      const execResult = command.execute(payload, afterActionSuccess);
      if (execResult instanceof Promise) {
        await execResult;
      }
      set(stackAtom, [...get(stackAtom), {
        command,
        payload
      }]);

      // resetting redo stack
      set(redoStackAtom, []);
      return;
    }
  });
  const lastUndoableActionAtom = atom(get => {
    const stack = get(stackAtom);
    const last = stack[stack.length - 1];
    return last ? last.payload : null;
  });
  const lastRedoableActionAtom = atom(get => {
    const stack = get(redoStackAtom);
    const last = stack[stack.length - 1];
    return last ? last.payload : null;
  });
  return {
    dispatch,
    canUndo,
    canRedo,
    lastUndoableAction: lastUndoableActionAtom,
    lastRedoableAction: lastRedoableActionAtom
  };
};